import {InputHTMLAttributes, ReactElement, useRef} from "react";
import {Control, Controller, FieldValues} from "react-hook-form";
import styled from "styled-components";

import IconContrat from "@/assets/iconContrat.svg?react";
import IconTrash from "@/assets/iconTrash.svg?react";
import IconUpload from "@/assets/iconUpload.svg?react";
import {quicksandSemiBold} from "@/GlobalStyle";

const UploaderFileStyled = styled.div`
  border: 2px dashed ${({theme}) => theme.colors.mediumSecondary};

  padding: 10px 15px;

  &:hover {
    cursor: pointer;
    background-color: ${({theme}) => theme.colors.mediumSecondary};
    & p {
      color: white;
    }
    & svg {
      fill: white;
    }
  }
`;

const IconLabelContentStyled = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  & svg {
    width: 1.5rem;
    height: 1.5rem;
    fill: ${({theme}) => theme.colors.darkPrimary};
  }
`;
const InputFileStyled = styled.input`
  display: none;
`;

const LabelStyled = styled.p`
  color: ${({theme}) => theme.colors.darkPrimary};
  ${quicksandSemiBold}
  text-align: center;
`;

const FileListStyled = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 10px;
  margin: 10px 0;
`;

const FileItemStyled = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  & svg {
    width: 1.5rem;
    height: 1.5rem;
    fill: ${({theme}) => theme.colors.darkPrimary};
  }
`;

const IconTrashStyled = styled(IconTrash)`
  && {
    width: 1rem;
    height: 1rem;
    fill: ${({theme}) => theme.colors.alert};
    &:hover {
      transform: scale(1.1);
      transition: transform 0.2s ease-in-out;
      cursor: pointer;
    }
  }
`;

export function InputFileCustom({
  ...props
}: InputHTMLAttributes<HTMLInputElement> & {
  control: Control<FieldValues>;
  name: string;
  label?: string;
  accept?: string;
}): ReactElement {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleClick = (): void => {
    // Déclencher le clic sur l'input file
    inputRef.current?.click();
  };
  return (
    <Controller
      defaultValue={[]}
      {...props}
      render={({field}) => {
        const handleFileChange = (
          e: React.ChangeEvent<HTMLInputElement>
        ): void => {
          const files = e.target.files;
          if (files && files.length > 0) {
            // Convertir FileList en Array de File
            const filesArray = Array.from(files);
            field.onChange([...field.value, ...filesArray]);
          } else {
            field.onChange([]);
          }
        };
        // Destructurer field pour exclure 'value' qui ne peut pas être utilisé sur input[type="file"]
        const {value, onChange, ...fieldProps} = field;

        return (
          <>
            <UploaderFileStyled onClick={handleClick}>
              <IconLabelContentStyled>
                <IconUpload />
                <LabelStyled>
                  {props?.label || "Ajouter un fichier"}
                </LabelStyled>
              </IconLabelContentStyled>

              <InputFileStyled
                type="file"
                {...fieldProps}
                accept={props?.accept || "*"} // Ajouter l'attribut accept
                ref={inputRef}
                onChange={e => handleFileChange(e)}
                onClick={e => {
                  // Empêcher la propagation pour éviter les doubles clics
                  e.stopPropagation();
                }}
              />
            </UploaderFileStyled>

            <FileListStyled>
              {value?.map((file: File, index: number) => (
                <FileItemStyled key={file?.name}>
                  <IconContrat />
                  <LabelStyled>{file?.name}</LabelStyled>
                  <IconTrashStyled
                    onClick={() => {
                      onChange(
                        field.value.filter((_: File, i: number) => i !== index)
                      );
                    }}
                  />
                </FileItemStyled>
              ))}
            </FileListStyled>
          </>
        );
      }}></Controller>
  );
}
